import React, { Fragment, useState } from "react";
import CardNew from "components/molecules/Card/CardNew";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";
import usePermissions from "hooks/usePermissions";
import Hr from "components/atoms/Hr/Hr";
import Badge from "components/atoms/Badge/Badge";
import { DateTimeFormat, formatDate } from "helpers/dateTimeHelper";
import { formatPrice } from "helpers/commonHelpers";
import { useGetQuotationActionHistory } from "api/queries/shipments/useGetQuotationActionHistory";
import { FirstFourApprovers } from "pages/shipper/ShipmentQuotesV2/components/ApprovalFlow/components/ApprovalFlowHelpers";
import InvitedApproversModal from "pages/shipper/ShipmentQuotesV2/components/ApprovalFlow/components/InvitedApproversModal";

/**
 * Formats the user string to a clickable email link
 * @param {*} userString - The user string to format; e.g. "John Doe (johndoe@example.com)"
 * @returns the formatted user string
 */
function formatUserString(userString) {
  const split = userString.split(", ") ?? [];
  return (
    <>
      {split.map((approver, i) => {
        const [name, email] = approver.replaceAll(")", "").split(" (");
        return (
          <span key={i}>
            <a href={`mailto:${email}`}>{name}</a>
            {i === split.length - 1 ? "" : ", "}
          </span>
        );
      })}
    </>
  );
}

function ApprovalLog({ shipmentId }) {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const parsedPermissions = JSON.parse(permissions || "{}");
  const { full_name } = parsedPermissions;

  const [showInvitedApproversModal, setShowInvitedApproversModal] = useState(false);

  const { data: approvalLog, isLoading } = useGetQuotationActionHistory({
    shipmentId,
    options: { select: (data) => data.results },
  });

  const renderLogEntry = (entry, idx) => {
    const {
      action,
      comment,
      comments,
      quotation_detail,
      requested_approvers,
      timestamp,
      user_full_name,
      action_display,
      is_transferred,
      amount_info,
      action_type, // "1" = pre booking, "2" = post booking
    } = entry;

    const { company_name, reference_number, total_quoted_amount, currency_code } = quotation_detail;
    const author = user_full_name === full_name ? "You" : user_full_name;
    const isApprovalRequest = action === "1" || action === "5";
    const entryComment = comment || null;
    const lastEntry = idx === approvalLog.length - 1;

    const renderCostItems = (additionalCosts) => {
      if (!additionalCosts || additionalCosts.length === 0) return "";

      const formattedCosts = additionalCosts.map((cost) => `${cost.amount}${cost.currency_code}`);

      // Join items with commas and "and" before the last item
      if (formattedCosts.length === 1) return formattedCosts[0];
      if (formattedCosts.length === 2)
        return formattedCosts.join(` ${intl.formatMessage({ id: "general.and", defaultMessage: "and" })} `);
      return `${formattedCosts.slice(0, -1).join(", ")}, ${intl.formatMessage({
        id: "general.and",
        defaultMessage: "and",
      })} ${formattedCosts[formattedCosts.length - 1]}`;
    };

    let actionDisplay = "";
    let type = "";
    switch (action) {
      case "1":
        actionDisplay = "requested approval for";
        type = "secondary";
        break;
      case "2":
        actionDisplay = action_display === "Booked" ? "booked" : "approved approval request for";
        type = "primary";
        break;
      case "3":
        actionDisplay = "rejected approval request for";
        type = "danger";
        break;
      case "4":
        actionDisplay = "canceled approval request for";
        type = "danger";
        break;
      case "5":
        actionDisplay = "requested pre-approval for";
        type = "secondary";
        break;
      default:
        break;
    }

    const handleClickOthers = () => {
      setShowInvitedApproversModal(true);
    };

    const postBookingCharges = action === "4" ? amount_info?.filter?.((cost) => !!cost?.approved_by_shipper) : amount_info;

    return (
      <Fragment key={idx}>
        <InvitedApproversModal
          show={showInvitedApproversModal}
          onHide={() => setShowInvitedApproversModal(false)}
          requested_approvers={requested_approvers}
        />
        {is_transferred &&
          comments.length > 0 &&
          comments.map(({ comment: commentText, submit_date, commenter, transferred_users }) => (
            <div key={submit_date}>
              <div className="d-flex justify-content-between">
                <div>
                  <p className="font-b3">{commenter} transferred approval request to:</p>
                  {transferred_users.map((approver) => formatUserString(approver))}
                </div>
              </div>
              {commentText && (
                <div className="font-weight-bold mt-2 font-b3">
                  Comments: <span className="font-weight-normal">{commentText}</span>
                </div>
              )}
              <div className="d-flex justify-content-between mt-2">
                <div style={{ width: "fit-content", display: "inline-block" }}>
                  <Badge
                    type={type}
                    label={intl.formatMessage({
                      id: "general.transferred",
                      defaultMessage: "Transferred",
                    })}
                  />
                </div>
                <div className="d-inline text-secondary font-b3 text-right">
                  {formatDate(submit_date, DateTimeFormat.fullDateTimeFormat)}
                </div>
              </div>
              <Hr />
            </div>
          ))}
        <div key={timestamp}>
          <div className="d-flex justify-content-between">
            <div>
              <div className="font-b3">
                {author} {actionDisplay}{" "}
                {action_type === "2" || (amount_info && amount_info.length > 0)
                  ? `${intl.formatMessage({
                      id: "shipment-quotes.components.approval-log.additionalCostOf",
                      defaultMessage: "additional cost of",
                    })} ${renderCostItems(amount_info)}`
                  : `${formatPrice(total_quoted_amount, currency_code)} quote ${reference_number} from ${company_name}.`}
              </div>
              {amount_info &&
                action_type === "2" &&
                (action !== "4" || amount_info.some((cost) => !!cost?.approved_by_shipper)) && (
                  <div>
                    <div className="font-weight-bold mt-2 font-b3">
                      <FormattedMessage id="quote-request.postBookingCharges" defaultMessage="Post booking charges" />:{" "}
                      <span className="font-weight-normal">
                        {postBookingCharges
                          .map((cost) => `${cost.description} (${formatPrice(cost.amount, cost.currency_code)})`)
                          .join(", ")}
                      </span>
                    </div>
                  </div>
                )}
              {isApprovalRequest && (
                <>
                  <div className="font-weight-bold mt-1 font-b3">Requested approvers:</div>
                  <FirstFourApprovers approvers={requested_approvers} />
                  {requested_approvers?.length > 4 && (
                    <>
                      ,{" "}
                      <span className="font-regular text-brand-tertiary-400 cursor-pointer" onClick={handleClickOthers}>
                        <FormattedMessage
                          id="quote-request.approvalFlow.andMore"
                          defaultMessage="and {users} more"
                          values={{ users: requested_approvers?.length - 4 }}
                        />
                      </span>
                    </>
                  )}
                  {/* <div>{requested_approvers.map((approver) => formatUserString(approver))}</div> */}
                </>
              )}
              {entryComment && (
                <div className="font-weight-bold mt-2 font-b3">
                  Comments: <span className="font-weight-normal">{entryComment}</span>
                </div>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-between mt-2">
            <div style={{ width: "fit-content", display: "inline-block" }}>
              <Badge type={type} label={action_display} />
            </div>
            <div className="d-inline text-secondary font-b3 text-right">
              {formatDate(timestamp, DateTimeFormat.fullDateTimeFormat)}
            </div>
          </div>

          {!lastEntry && <Hr />}
        </div>
      </Fragment>
    );
  };

  if (!approvalLog || approvalLog.length === 0) return null;
  return (
    <CardNew
      height="280px"
      isLoading={isLoading}
      title={<FormattedMessage id="shipment-quotes.components.approval-log.approvalLog" defaultMessage="Approval Log" />}
    >
      <CardNew.Body className="overflow-auto p-0">
        {approvalLog.map((entry, idx) => renderLogEntry(entry, idx))}
      </CardNew.Body>
    </CardNew>
  );
}

ApprovalLog.propTypes = {
  shipmentId: PropTypes.string.isRequired,
};

export default ApprovalLog;
